import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../hooks/useSiteSettings';
import ImagePreviewModal from '../components/ImagePreviewModal';
import { getNewsImageSrcs } from '../utils/newsImages';
import './NewsPage.css';

const NewsPage = () => {
  const { site_name } = useSiteSettings();
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewAlt, setPreviewAlt] = useState('');

  useEffect(() => {
    const fetchWorks = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/news`);
        if (response.ok) {
          const data = await response.json();
          const published = Array.isArray(data) ? data.filter(item => item.status === 'published') : [];
          // الأعمال: المنشورات التي تحتوي على صور
          const items = published
            .map(item => ({
              ...item,
              images: getNewsImageSrcs(item)
            }))
            .filter(item => item.images.length > 0);
          setWorks(items);
        }
      } catch (error) {
        console.error('خطأ في جلب الأعمال:', error);
        setWorks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWorks();
  }, []);

  const totalImages = works.reduce((sum, work) => sum + work.images.length, 0);

  const openPreview = (img, alt) => {
    setPreviewImage(img);
    setPreviewAlt(alt);
  };

  return (
    <>
      <Helmet>
        <title>أعمالنا | {site_name || ''}</title>
        <meta name="description" content="استعرض صور أعمالنا ومشاريعنا وإنجازاتنا" />
      </Helmet>
      
      <div className="news-page">
        {/* Hero Section */}
        <section className="news-hero">
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title">أعمالنا</h1>
              <p className="hero-subtitle">
                استعرض صور أعمالنا وإنجازاتنا من خلال المنشورات المرفوعة
              </p>
              <div className="hero-stats">
                <div className="stat-item">
                  <span className="stat-number">{works.length}</span>
                  <span className="stat-label">عمل منشور</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{totalImages}</span>
                  <span className="stat-label">صورة</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="news-content">
          <div className="container">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>جاري تحميل الأعمال...</p>
              </div>
            ) : works.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🖼️</div>
                <h3>لا توجد أعمال حالياً</h3>
                <p>لم يتم رفع أي صور منشورة بعد</p>
              </div>
            ) : (
              <div className="works-gallery">
                {works.map(work => (
                  <div key={work.id} className="work-block">
                    <div className="work-block-header">
                      <h3 className="work-block-title">{work.title}</h3>
                      <Link to={`/news/${work.id}`} className="view-more-btn">
                        <span>عرض المزيد</span>
                        <i className="fas fa-arrow-left"></i>
                      </Link>
                    </div>
                    {work.summary && (
                      <p className="work-block-desc">{work.summary}</p>
                    )}
                    <div className="works-gallery-grid">
                      {work.images.map((img, index) => (
                        <div
                          className="works-gallery-item"
                          key={index}
                          role="button"
                          tabIndex={0}
                          onClick={() => openPreview(img, `${work.title} - ${index + 1}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openPreview(img, `${work.title} - ${index + 1}`);
                            }
                          }}
                        >
                          <img src={img} alt={`${work.title} - ${index + 1}`} loading="lazy" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      <ImagePreviewModal
        open={!!previewImage}
        image={previewImage}
        alt={previewAlt}
        onClose={() => {
          setPreviewImage(null);
          setPreviewAlt('');
        }}
      />
    </>
  );
};

export default NewsPage;
